import { ChangeDetectionStrategy, Component, Type, computed, input, output } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { LucideCircleAlert, LucideCircleCheck, LucideCircleX, LucideInfo, LucideX } from '@lucide/angular';

export type AlertVariant = 'success' | 'warning' | 'error' | 'info';

const ALERT_ICONS: Record<AlertVariant, Type<unknown>> = {
  success: LucideCircleCheck,
  warning: LucideCircleAlert,
  error: LucideCircleX,
  info: LucideInfo,
};

@Component({
  selector: 'ds-alert',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgComponentOutlet],
  host: { '[class]': 'classes()', '[attr.role]': 'role()' },
  template: `
    <span class="alert__icon" aria-hidden="true">
      <ng-container
        [ngComponentOutlet]="icon()"
        [ngComponentOutletInputs]="{ size: 26, strokeWidth: 2.25 }"
      />
    </span>
    <div class="alert__content">
      @if (title()) {
        <strong class="alert__title">{{ title() }}</strong>
      }
      <div class="alert__message"><ng-content /></div>
    </div>
    @if (dismissible()) {
      <button
        type="button"
        class="alert__dismiss"
        [attr.aria-label]="dismissLabel()"
        (click)="dismissed.emit()"
      >
        <ng-container
          [ngComponentOutlet]="closeIcon"
          [ngComponentOutletInputs]="{ size: 22, strokeWidth: 2.5 }"
        />
      </button>
    }
  `,
  styles: `
    :host {
      display: flex;
      align-items: flex-start;
      gap: var(--space-3);
      padding: var(--space-4);
      border-radius: var(--radius-md);
      border: 1.5px solid;
      font-size: var(--text-md);
    }

    :host(.alert--success) {
      background: var(--color-success-tint);
      border-color: var(--color-success-border);
      .alert__icon, .alert__title { color: var(--color-success-strong); }
    }
    :host(.alert--warning) {
      background: var(--color-warning-tint);
      border-color: var(--color-warning-border);
      .alert__icon, .alert__title { color: var(--color-warning-strong); }
    }
    :host(.alert--error) {
      background: var(--color-error-tint);
      border-color: var(--color-error-border);
      .alert__icon, .alert__title { color: var(--color-error-strong); }
    }
    :host(.alert--info) {
      background: var(--color-info-tint);
      border-color: var(--color-info-border);
      .alert__icon, .alert__title { color: var(--color-info-strong); }
    }

    .alert__icon {
      flex-shrink: 0;
      display: inline-flex;
      margin-top: 1px;
    }

    .alert__content {
      flex: 1;
      min-width: 0;
      color: var(--color-text);
    }

    .alert__title {
      display: block;
      margin-bottom: var(--space-1);
      font-weight: var(--font-weight-bold);
    }

    .alert__message :where(p) {
      margin: 0;
    }

    .alert__dismiss {
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: var(--touch-target);
      height: var(--touch-target);
      margin: calc(var(--space-2) * -1);
      margin-left: 0;
      border: 0;
      background: transparent;
      border-radius: var(--radius-full);
      color: var(--color-text-secondary);
      cursor: pointer;
      transition: background-color var(--motion-fast);

      &:hover {
        background: rgba(0, 0, 0, 0.06);
      }
    }
  `,
})
export class AlertComponent {
  variant = input<AlertVariant>('info');
  title = input<string | undefined>(undefined);
  dismissible = input(false);
  dismissLabel = input('Tutup pesan');
  dismissed = output<void>();

  protected readonly closeIcon = LucideX;
  protected classes = computed(() => `alert--${this.variant()}`);
  protected icon = computed(() => ALERT_ICONS[this.variant()]);
  /** Errors/warnings interrupt assistive tech; success/info announce politely. */
  protected role = computed(() =>
    this.variant() === 'error' || this.variant() === 'warning' ? 'alert' : 'status',
  );
}
